import { useState } from "react";
import { useParams, useRouterState } from "@tanstack/react-router";
import { usePlayer } from "../api/queries";
import { carded, theoNumber } from "../in-play/money";
import { formatDuration } from "../in-play/quick-filters";
import { filterTripSessions, startTimeText, tripDurationText } from "../players/profile-logic";
import { useTripSessions, type TripSessionRow } from "../players/profile-api";
import { Breadcrumb } from "../ui/Breadcrumb";
import { PButton } from "../ui/PButton";
import { PagedDataView, type DataViewLayout } from "../ui/PagedDataView";

// PORT OF `players/_pages/player-trip`, OPENED FROM A TRIP'S "SESSIONS" BUTTON:
// `/players/$playerId/$tripId` (OR UNDER `/in-play`). THE PLAYER CARD (HOST AND THE TRIP'S
// DATES), THE SESSION COUNT AND THE TRIP'S DAILY-AVERAGE ACTUAL AND THEO TILES, OVER THE
// TRIP'S SESSIONS. THE TILES READ THE TRIP ROW THE PROFILE PASSED IN HISTORY STATE, AS ANGULAR
// DOES, SO A RELOADED OR PASTED LINK SHOWS THEM EMPTY.
export function PlayerTripPage() {
  const { playerId = "", tripId = "" } = useParams({ strict: false });
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const trip = useRouterState({ select: (s) => s.location.state.trip });
  const fromInPlay = pathname.startsWith("/in-play/");
  const parent = fromInPlay ? { label: "In-Play", to: "/in-play" } : { label: "Players", to: "/players" };
  const player = usePlayer({ by: "card", cardId: playerId });
  const sessions = useTripSessions(playerId, Number(tripId));
  const [layout, setLayout] = useState<DataViewLayout>("grid");
  const [search, setSearch] = useState("");
  const all = sessions.data ?? [];
  const rows = filterTripSessions(all, search);

  const actual = trip?.totalDailyActual ?? 0;
  const actualShown = carded(trip?.totalDailyActual);
  return (
    <div className="parent">
      <div className="child">
        <Breadcrumb
          items={[parent, { label: playerId, to: fromInPlay ? `/in-play/${encodeURIComponent(playerId)}` : `/players/${encodeURIComponent(playerId)}` }, { label: tripId }]}
        />
        <div className="grid p-2">
          <div className="col-12 md:col-4">
            {/* `player-trip-card` */}
            <div className="card shadow-1 mb-0 h-full">
              <div className="flex justify-content-between align-items-center mb-3 vertical-align-middle">
                <div className="text-lg font-bold pl-2">{(player.data?.name ?? "").toUpperCase()}</div>
              </div>
              <div className="p-2">
                <div className="w-full flex-column">
                  <div>
                    <div className="font-bold">Host:</div>
                    <div>{player.data?.host ? player.data.host.hostName : "n/a"}</div>
                  </div>
                  <div className="flex flex-column pt-3">
                    <div className="font-bold">Trip:</div>
                    {tripDurationText(sessions.data)}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-12 md:col-2">
            <Tile title="Total Sessions" className=" bg-primary-card">
              <div className="font-bold">{sessions.data ? all.length : ""}</div>
            </Tile>
          </div>
          <div className="col-12 md:col-3">
            <Tile
              title="Daily Average Actual"
              className={actual > 0 ? " bg-green-500-card" : actual < 0 ? " bg-red-500-card" : ""}
              watermark={actualShown?.arrow ? (actualShown.tone === "up" ? "arrow_upward" : "arrow_downward") : undefined}
            >
              <div className="font-bold">{actual > 0 ? actualShown?.text : actual < 0 ? (actualShown?.text ?? "-$") : "$0"}</div>
            </Tile>
          </div>
          <div className="col-12 md:col-3">
            <Tile title="Daily Average Theo" className=" bg-primary-700-card">
              <div className="font-bold">
                <span className="ml-2">$</span>
                {theoNumber(trip?.totalDailyTheo)}
              </div>
            </Tile>
          </div>
        </div>
      </div>
      <div className="child-grow mt-3 card p-0 shadow-1 overflow-x-hidden overflow-y-auto">
        <PagedDataView
          plain
          rows={rows}
          page={1}
          totalPages={1}
          totalRows={rows.length}
          onPage={() => {}}
          layout={layout}
          onLayout={setLayout}
          emptyMessage={sessions.isPending ? "" : "No results found"}
          // THE SESSION TEMPLATE'S OWN HEADER: A SEARCH (BUTTON, THEN FIELD) IN THE MIDDLE EIGHT COLUMNS,
          // THE LIST / GRID TOGGLE AT THE RIGHT; IT FILTERS AS YOU TYPE
          header={(toggle) => (
            <div className="grid pl-2 pr-2">
              <div className="col-0 md:col-2" />
              <div className="col-8 md:col-8">
                <div className="flex flex-row align-items-center md:justify-content-center font-bold text-sm md:text-lg">
                  <div className="p-inputgroup flex">
                    {/* THE LIST FILTERS AS YOU TYPE, SO THE BUTTON HAS NOTHING LEFT TO DO (AS IN ANGULAR) */}
                    <PButton icon="search" ariaLabel="Search sessions" />
                    <input type="text" className="p-inputtext bg-input-field" placeholder="Search" aria-label="Search sessions" value={search} onChange={(e) => setSearch(e.target.value)} />
                  </div>
                </div>
              </div>
              <div className="col-4 md:col-2">
                <div className="flex justify-content-end">{toggle}</div>
              </div>
            </div>
          )}
          rowKey={(s) => `${s.id ?? ""}-${s.startTime ?? ""}`}
          render={(s, l) => <TripSessionCard session={s} layout={l} />}
        />
      </div>
    </div>
  );
}

// `watermark`: THE SAME ARROW AS THE PROFILE'S ACTUAL TILES (ANGULAR DRAWS IT INLINE BEFORE THE AMOUNT) - A LARGE
// ARROW BEHIND THE AMOUNT IN A DARKER SHADE OF THE TILE. THIS TILE IS SHORT AND ITS AMOUNT SITS HIGH, SO THE ARROW
// IS CENTERED ON THE WHOLE TILE AND SIZED TO ITS HEIGHT RATHER THAN CENTERED ON THE AMOUNT
function Tile({ title, className, watermark, children }: { title: string; className: string; watermark?: string; children: React.ReactNode }) {
  return (
    <div className={`h-full${className}`}>
      <div className="p-card p-component h-full overflow-hidden relative">
        {watermark && (
          <div
            className="material-symbols-rounded absolute"
            aria-hidden="true"
            style={{ fontSize: "10rem", lineHeight: 1, color: "#000000", opacity: 0.18, top: "50%", left: "50%", transform: "translate(-50%, -50%)", pointerEvents: "none" }}
          >
            {watermark}
          </div>
        )}
        <div className="p-card-body relative">
          <div className="p-card-title">{title}</div>
          <div className="p-card-content">
            <div className="w-full flex align-items-center justify-content-center">
              <div className="chart-font-size w-10rem">{children}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// `player-trip-session-template`: PROPERTY, MACHINE AND LOCATION, THE SESSION'S ACTUAL (FROM
// `gains`) AND THEO (FROM `theoWin`), ITS START TIME AND DURATION
function TripSessionCard({ session: s, layout }: { session: TripSessionRow; layout: DataViewLayout }) {
  const shown = carded(s.gains);
  const tone = shown?.tone === "up" ? "text-green-600" : "text-red-600";
  const money = (
    <div className="flex flex-column justify-content-end align-items-end w-15rem">
      {shown ? (
        <div className="flex flex-row align-items-center font-bold">
          {shown.arrow && <span className={`material-symbols-rounded ${tone}`}>{shown.tone === "up" ? "arrow_upward" : "arrow_downward"}</span>}
          <div className={tone}>{shown.text}</div>
        </div>
      ) : (
        <div />
      )}
      <div className="font-bold">
        <span>Theo:</span>
        <span className="ml-2">$</span>
        {theoNumber(s.theoWin)}
      </div>
    </div>
  );
  const start = (
    <div className="flex flex-row align-items-center">
      <div className="mr-2">
        <span className="material-symbols-outlined">schedule</span>
      </div>
      <div>{startTimeText(s.startTime)}</div>
    </div>
  );
  const duration = (
    <div className="flex flex-row align-items-center">
      <span className="material-symbols-rounded">avg_pace</span>
      <div className="ml-2">{formatDuration(s.duration)}</div>
    </div>
  );
  const machine = (
    <div className="flex flex-column justify-content-start w-20rem">
      <div className="font-bold">{s.description}</div>
      <div className="mt-1 text-sm">{s.location}</div>
    </div>
  );
  if (layout === "list") {
    return (
      <div className="border-bottom-1 surface-border pl-2 pr-2">
        <div className="flex flex-row align-items-center justify-content-between">
          {s.siteName && <div className="font-bold text-sm">{s.siteName}</div>}
          <div className="mt-1">{machine}</div>
          <div className="flex flex-column justify-content-start w-10rem">
            <div>{start}</div>
            <div>{duration}</div>
          </div>
          {money}
        </div>
      </div>
    );
  }
  return (
    <div className="p-2 border-1 surface-border surface-card border-round flex flex-column">
      {s.siteName && <div className="font-bold text-sm">{s.siteName}</div>}
      <div className="mt-1">
        <div className="flex flex-row align-items-center justify-content-between">
          {machine}
          {money}
        </div>
      </div>
      <div className="mt-3 flex align-items-center justify-content-between">
        <div>{start}</div>
        <div>{duration}</div>
      </div>
    </div>
  );
}
